/**
 * <s-size-finder>
 * Live size recommendation (snippets/size-finder.liquid).
 * - Body estimate from height (cm) and weight (kg):
 *   chest ≈ 0.73·kg + 0.25·cm − 3, hip ≈ 0.5·kg + 0.3·cm + 6 (centimetres).
 * - Target garment circumference = body + data-ease (cut) + fit offset
 *   (tight −8, regular 0, loose +10). The chart row (½ width × 2) closest to the
 *   target wins; on a near tie the larger size wins.
 * - Inputs persist in localStorage ('ma-size-profile'), so every product page
 *   shows "Deine Größe: L" on the trigger once the shopper has used it.
 * - Apply clicks the matching size radio in the page's variant picker.
 * - Model twin: [data-twin] cards (data-gender, data-height, data-name) from the Model-Info list;
 *   the card closest to the shopper's height within the chosen gender gets data-active.
 *   Gender buttons [data-size-finder-gender] switch it; the choice persists with the profile.
 */
const STORAGE_KEY = 'ma-size-profile';
const FIT_OFFSET = { tight: -8, regular: 0, loose: 10 };

const normalizeSize = (value) => String(value ?? '')
  .trim()
  .toUpperCase()
  .replace(/^XXXXXL$/, '5XL')
  .replace(/^XXXXL$/, '4XL')
  .replace(/^XXXL$/, '3XL')
  .replace(/^XXL$/, '2XL');

class SSizeFinder extends HTMLElement {
  #ac = null;
  #chart = [];
  #profile = { height: 178, weight: 75, fit: 'regular', gender: '', build: '' };
  #best = null;
  #dialog = null;

  connectedCallback() {
    // Move the dialog to <body> so no product-page column, overflow or transform can clip it.
    this.#dialog = this.querySelector('dialog') ?? this.#dialog;
    if (this.#dialog && this.#dialog.parentElement !== document.body) document.body.append(this.#dialog);

    try {
      const raw = JSON.parse(this.querySelector('[data-size-chart]')?.textContent || '[]');
      this.#chart = raw
        .map((row) => ({ size: String(row.s), circ: Number.parseFloat(row.v) * 2 }))
        .filter((row) => row.size && Number.isFinite(row.circ));
    } catch {
      this.#chart = [];
    }
    if (!this.#chart.length) return;

    const stored = this.#readProfile();
    if (stored) this.#profile = { ...this.#profile, ...stored };

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#all('[data-size-finder-input]').forEach((input) => {
      const key = input.dataset.sizeFinderInput;
      if (key in this.#profile) input.value = String(this.#profile[key]);
      input.addEventListener('input', () => {
        this.#profile[key] = Number(input.value);
        if (key === 'height' && this.#one('[data-size-finder-build]') && this.#profile.build) this.#profile.weight = this.#weightFromBuild();
        this.#update(true);
      }, { signal });
    });

    this.#all('[data-size-finder-fit]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#profile.fit = button.dataset.sizeFinderFit;
        this.#update(true);
      }, { signal });
    });

    this.#all('[data-size-finder-gender]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#profile.gender = button.dataset.sizeFinderGender;
        this.#update(true);
      }, { signal });
    });

    this.#one('[data-size-finder-apply]')?.addEventListener('click', () => this.#apply(), { signal });

    // E (tag): plastic +/- keys step the matching range input ("height:1", "weight:-1") and reuse its input listener.
    this.#all('[data-sf-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const [key, delta] = button.dataset.sfStep.split(':');
        const input = this.#all(`[data-size-finder-input="${key}"]`)[0];
        if (!input) return;
        const next = Math.min(Number(input.max), Math.max(Number(input.min), Number(input.value) + Number(delta)));
        if (next === Number(input.value)) return;
        input.value = String(next);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }, { signal });
    });

    // F (wall): one key per chart size; #update marks the recommendation and the close runner-up.
    this.#all('[data-sf-wall]').forEach((wall) => {
      wall.replaceChildren(...this.#chart.map((row) => {
        const key = document.createElement('span');
        key.className = 'ma-sf-key';
        key.dataset.sfKey = row.size;
        key.textContent = row.size;
        return key;
      }));
    });

    // C (swipe): build buttons estimate weight from height (BMI 20 / 23.5 / 27); step buttons switch panels.
    this.#all('[data-size-finder-build]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#profile.build = button.dataset.sizeFinderBuild;
        this.#profile.weight = this.#weightFromBuild();
        this.#update(true);
      }, { signal });
    });
    this.#all('[data-sf-goto]').forEach((button) => {
      button.addEventListener('click', () => this.#goto(button.dataset.sfGoto), { signal });
    });
    // Re-pick the twin on open, so it follows the gallery photo the shopper looked at last.
    this.#dialog?.addEventListener('toggle', (event) => {
      this.#updateTwin();
      if (event.newState === 'open') this.#goto('1');
    }, { signal });

    this.#update(false, Boolean(stored));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    // A re-rendered picker brings its own dialog; drop the moved one so ids stay unique.
    if (this.#dialog?.open) this.#dialog.close();
    this.#dialog?.remove();
    this.#dialog = null;
  }

  /** Looks in the trigger area and in the moved dialog. */
  #one(selector) {
    return this.querySelector(selector) ?? this.#dialog?.querySelector(selector) ?? null;
  }

  #all(selector) {
    return [...this.querySelectorAll(selector), ...(this.#dialog?.querySelectorAll(selector) ?? [])];
  }

  #readProfile() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      return value && typeof value === 'object' ? value : null;
    } catch {
      return null;
    }
  }

  #saveProfile() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#profile));
    } catch {
      /* storage unavailable: recommendation still works for this visit */
    }
  }

  #recommend() {
    const { height, weight, fit } = this.#profile;
    const body = this.dataset.measure === 'hip'
      ? 0.5 * weight + 0.3 * height + 6
      : 0.73 * weight + 0.25 * height - 3;
    const target = body + Number(this.dataset.ease || 18) + (FIT_OFFSET[fit] ?? 0);

    const ranked = this.#chart
      .map((row, index) => ({ ...row, index, diff: Math.abs(row.circ - target) }))
      .sort((a, b) => (Math.abs(a.diff - b.diff) < 1 ? b.index - a.index : a.diff - b.diff));
    const best = ranked[0];
    const runnerUp = ranked[1] && ranked[1].diff - best.diff < 2.5 ? ranked[1] : null;
    return { best, runnerUp };
  }

  #update(fromUser, showOnTrigger = fromUser) {
    const { fit } = this.#profile;

    this.#all('[data-size-finder-out]').forEach((out) => {
      out.textContent = String(this.#profile[out.dataset.sizeFinderOut] ?? '');
    });
    this.#all('[data-size-finder-fit]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.sizeFinderFit === fit));
    });

    this.#all('[data-size-finder-build]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.sizeFinderBuild === this.#profile.build));
    });
    // A/B: silhouettes scale with these (unitless) custom properties.
    this.#dialog?.style.setProperty('--sf-height', String(this.#profile.height));
    this.#dialog?.style.setProperty('--sf-weight', String(this.#profile.weight));

    const { best, runnerUp } = this.#recommend();
    const changed = this.#best?.size !== best.size;
    this.#best = best;

    const sizeEl = this.#one('[data-size-finder-size]');
    if (sizeEl && changed) {
      sizeEl.textContent = best.size;
      if (fromUser && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        sizeEl.animate([{ scale: 0.6, opacity: 0 }, { scale: 1.12, opacity: 1 }, { scale: 1, opacity: 1 }], { duration: 380, easing: 'cubic-bezier(0.2,0.7,0.2,1)' });
      }
    }

    this.#all('[data-sf-key]').forEach((key) => {
      key.toggleAttribute('data-active', key.dataset.sfKey === best.size);
      key.toggleAttribute('data-near', Boolean(runnerUp) && key.dataset.sfKey === runnerUp.size);
    });

    const fill = (template, values) => Object.entries(values)
      .reduce((text, [key, value]) => text.replaceAll(`__${key}__`, value), template || '');

    const measure = this.#one('[data-size-finder-measure]');
    if (measure) measure.textContent = fill(this.dataset.tplMeasure, { CM: String(Math.round(best.circ)) });

    const radio = this.#radioFor(best.size);
    const soldOut = radio ? radio.disabled : false;
    const alt = this.#one('[data-size-finder-alt]');
    if (alt) {
      if (soldOut) {
        alt.textContent = this.dataset.tplSoldOut || '';
      } else if (runnerUp) {
        const template = runnerUp.index > best.index ? this.dataset.tplLooser : this.dataset.tplTighter;
        alt.textContent = fill(template, { SIZE: runnerUp.size });
      } else {
        alt.textContent = '';
      }
    }

    const apply = this.#one('[data-size-finder-apply]');
    const applyLabel = this.#one('[data-size-finder-apply-label]');
    if (applyLabel) applyLabel.textContent = fill(this.dataset.tplApply, { SIZE: best.size });
    if (apply) apply.disabled = !radio || soldOut;

    this.#updateTwin();

    if (fromUser) this.#saveProfile();
    const trigger = this.#one('[data-size-finder-trigger]');
    if (trigger && showOnTrigger) trigger.textContent = fill(this.dataset.tplYours, { SIZE: best.size });
  }

  #updateTwin() {
    const cards = this.#all('[data-twin]');
    if (!cards.length) return;
    const genders = new Set(cards.map((card) => card.dataset.gender));
    // No saved choice yet: follow the model photo active in the gallery (tracked by <s-lineup-gender>).
    let gender = this.#profile.gender || document.querySelector('s-lineup-gender')?.dataset.gender || '';
    if (!genders.has(gender)) gender = genders.has(this.dataset.gender) ? this.dataset.gender : cards[0].dataset.gender;
    this.#all('[data-size-finder-gender]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.sizeFinderGender === gender));
    });

    const height = Number(this.#profile.height);
    const pool = cards.filter((card) => card.dataset.gender === gender);
    const twin = pool.reduce((best, card) => (
      Math.abs(Number(card.dataset.height) - height) < Math.abs(Number(best.dataset.height) - height) ? card : best
    ), pool[0]);
    cards.forEach((card) => card.toggleAttribute('data-active', card === twin));

    const diff = height - Number(twin.dataset.height);
    let template = this.dataset.tplTwinSame;
    if (diff > 0) template = this.dataset.tplTwinTaller;
    else if (diff < 0) template = this.dataset.tplTwinShorter;
    const out = twin.querySelector('[data-twin-diff]');
    if (out) out.textContent = (template || '').replaceAll('__CM__', String(Math.abs(diff))).replaceAll('__NAME__', twin.dataset.name || '');
  }

  #weightFromBuild() {
    const bmi = { slim: 20, regular: 23.5, broad: 27 }[this.#profile.build] ?? 23.5;
    const metres = Number(this.#profile.height) / 100;
    return Math.round(bmi * metres * metres);
  }

  /** C (swipe): shows panel `step` ("1"–"4") and moves focus to its heading. */
  #goto(step) {
    const steps = this.#one('[data-sf-steps]');
    if (!steps) return;
    steps.dataset.step = step;
    requestAnimationFrame(() => steps.querySelector(`[data-sf-panel="${step}"] [data-sf-focus]`)?.focus({ preventScroll: true }));
  }

  #radioFor(size) {
    const picker = this.closest('s-variant-picker') || document;
    const wanted = normalizeSize(size);
    return [...picker.querySelectorAll('input[type="radio"]')].find((input) => normalizeSize(input.value) === wanted) || null;
  }

  #apply() {
    if (!this.#best) return;
    const radio = this.#radioFor(this.#best.size);
    if (!radio || radio.disabled) return;
    this.#saveProfile();
    this.#dialog?.close();
    if (!radio.checked) radio.click();
  }
}

if (!customElements.get('s-size-finder')) customElements.define('s-size-finder', SSizeFinder);
